import { useCallback, useEffect, useMemo, useState } from "react";
import { CampaignType } from "@adlywise/shared";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { usePageTitle } from "@/components/common/AppShell";
import WizardShell from "@/components/common/WizardShell";
import Button from "@/components/common/Button";
import { useWizard } from "@/hooks/useWizard";
import { toApiDateTime } from "@/utils/dateUtils";
import {
  toFormState,
  toNullableInt,
  toNullableFloat,
  toCreativeCooldownsPayload,
  minCampaignCpm,
  popFormErrors,
  toPopCampaignPayload,
} from "@/components/campaigns/wizard/campaignFormUtils";
import PopTargetStep from "@/components/campaigns/wizard/PopTargetStep";
import Step1Basics from "@/components/campaigns/wizard/Step1Basics";
import Step2Target from "@/components/campaigns/wizard/Step2Target";
import Step3Creatives from "@/components/campaigns/wizard/Step3Creatives";
import Step5Review from "@/components/campaigns/wizard/Step5Review";
import CampaignSummarySidebar from "@/components/campaigns/wizard/CampaignSummarySidebar";

const STEPS = [
  { id: "basics", label: "Basics" },
  { id: "target", label: "Target" },
  { id: "creatives", label: "Creatives" },
  { id: "review", label: "Review" },
];
// A popunder campaign has no audiences or creatives: its target step is the
// pop configuration, and there is no creatives step.
const POP_STEPS = [
  { id: "basics", label: "Basics" },
  { id: "pop", label: "Popunder" },
  { id: "review", label: "Review" },
];

export default function CampaignBuilderPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const editingUuid = params.uuid ?? null;

  const initialStep = location.state?.initialStep ?? 0;
  const {
    campaigns,
    segments,
    addCampaign,
    updateCampaign,
    getCampaignCost,
    getCampaignStatistics,
    currentUser,
    submitCampaignForModeration,
    approveCampaign,
    downloadCampaignPlan,
    performRequest,
    refreshCampaigns,
    currentUserType,
    providers,
    providersLoading,
    providersError,
    refreshProviders,
  } = useApp();

  const editingCampaign = useMemo(
    () => (editingUuid ? campaigns.find((c) => c.uuid === editingUuid) : null),
    [campaigns, editingUuid],
  );

  const isEdit = Boolean(editingUuid);
  usePageTitle(isEdit ? "Edit campaign" : "Create campaign");

  const [form, setForm] = useState(() => toFormState(editingCampaign));
  const [isSaving, setIsSaving] = useState(false);
  const [isModerateSaving, setIsModerateSaving] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [budget, setBudget] = useState(null);
  const [budgetLoading, setBudgetLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [creatives, setCreatives] = useState([]);
  const [segmentCreatives, setSegmentCreatives] = useState({});

  const goBack = () => navigate("/dashboard/campaigns");

  const isPop = form.type === CampaignType.Popunder;
  const steps = isPop ? POP_STEPS : STEPS;

  const isStepValid = useCallback(
    (index) => {
      if (index === 0) return form.name.trim().length > 0;
      if (form.type === CampaignType.Popunder) {
        if (index === 1) return popFormErrors(form.pop, form.cpm).length === 0;
        return true;
      }
      if (index === 1 && form.type === CampaignType.Standard) {
        const selectedPublishers = form.metadata?.selectedPublishers ?? [];
        if (selectedPublishers.length === 0) return false;
        if (
          (Number(form.cpm) || 0) <
          minCampaignCpm(selectedPublishers, providers)
        )
          return false;
      }
      return true;
    },
    [form.name, form.type, form.metadata, form.pop, form.cpm, providers],
  );

  const wizard = useWizard({
    stepCount: steps.length,
    isStepValid,
    initialStep,
  });

  useEffect(() => {
    setForm(toFormState(editingCampaign));
  }, [editingCampaign]);

  useEffect(() => {
    performRequest("/creatives")
      .then((data) => setCreatives(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Failed to load creatives", err));
  }, [performRequest]);

  useEffect(() => {
    if (!editingCampaign?.uuid) {
      setSegmentCreatives({});
      return;
    }
    performRequest(`/campaigns/${editingCampaign.uuid}/creatives`)
      .then((data) => {
        if (data && typeof data === "object") setSegmentCreatives(data);
      })
      .catch((err) => console.error("Failed to load campaign creatives", err));
  }, [editingCampaign?.uuid, performRequest]);

  useEffect(() => {
    if (!editingCampaign?.uuid) {
      setBudget(null);
      return;
    }
    let cancelled = false;
    setBudgetLoading(true);
    getCampaignCost(editingCampaign.uuid)
      .then((cost) => {
        if (!cancelled) setBudget(cost);
      })
      .catch((error) => {
        console.error("Failed to load campaign cost", error);
        if (!cancelled) setBudget(null);
      })
      .finally(() => {
        if (!cancelled) setBudgetLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [editingCampaign?.uuid, getCampaignCost]);

  useEffect(() => {
    if (!editingCampaign?.uuid) {
      setStats(null);
      return undefined;
    }
    let cancelled = false;
    setStatsLoading(true);
    getCampaignStatistics(editingCampaign.uuid)
      .then((result) => {
        if (!cancelled) setStats(result);
      })
      .catch((error) => {
        console.error("Failed to load campaign statistics", error);
        if (!cancelled) setStats(null);
      })
      .finally(() => {
        if (!cancelled) setStatsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [editingCampaign?.uuid, getCampaignStatistics]);

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePopChange = (patch) => {
    setForm((prev) => ({ ...prev, pop: { ...prev.pop, ...patch } }));
  };

  const handleNumberChange = (field, value) => {
    if (value === "") {
      handleFieldChange(field, "");
    } else {
      const numericValue = Number(value);
      if (Number.isNaN(numericValue)) return;
      handleFieldChange(field, numericValue);
    }
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      const isTransit = form.type === CampaignType.Transit;
      const isPopunder = form.type === CampaignType.Popunder;
      if (isPopunder) {
        const errors = popFormErrors(form.pop, form.cpm);
        if (errors.length > 0) {
          window.alert(errors.join("\n"));
          setIsSaving(false);
          return;
        }
      } else if (!isTransit) {
        const selectedPublishers = form.metadata?.selectedPublishers ?? [];
        if (
          selectedPublishers.length === 0 ||
          Number(form.cpm) < minCampaignCpm(selectedPublishers, providers)
        ) {
          window.alert(
            "Select at least one provider and set the campaign CPM above the priciest selected provider's cost.",
          );
          setIsSaving(false);
          return;
        }
      }
      const basePayload = {
        ...form,
        defaultCreativeId: form.defaultCreativeId || null,
        startDate: toApiDateTime(form.startDate),
        endDate: toApiDateTime(form.endDate),
        cpm: Number(form.cpm) || 0,
      };
      // Remove the metadata sub-object from the spread — we attach it explicitly.
      delete basePayload.metadata;
      delete basePayload.pop;

      const payload = isPopunder
        ? toPopCampaignPayload(form, basePayload)
        : isTransit
          ? {
              ...basePayload,
              type: CampaignType.Transit,
              selectedSegments: [],
              metadata: {
                entrypoint: form.metadata.entrypoint,
                margin: Number(form.metadata.margin) || 0,
                qps: Number(form.metadata.qps) || 1,
                ...(form.metadata.timeoutMs !== "" &&
                form.metadata.timeoutMs != null
                  ? { timeoutMs: Number(form.metadata.timeoutMs) }
                  : {}),
                ...(form.metadata.filters
                  ? { filters: form.metadata.filters }
                  : {}),
                selectedPublishers: form.metadata.selectedPublishers,
                blockList: form.metadata.blockList,
                // The site allow-list is edited in step 2; it was previously
                // collected and then dropped here, so it never reached the server.
                whiteList: form.metadata.whiteList ?? [],
                // Keep the endpoint's public statistics token across an edit.
                ...(form.metadata.statsToken
                  ? { statsToken: form.metadata.statsToken }
                  : {}),
              },
            }
          : {
              ...basePayload,
              type: CampaignType.Standard,
              selectedSegments: form.selectedSegments,
              dailyFrequency: toNullableInt(form.dailyFrequency),
              weeklyFrequency: toNullableInt(form.weeklyFrequency),
              impressionsCap: toNullableInt(form.impressionsCap),
              budgetCap: toNullableFloat(form.budgetCap),
              dailyBudgetCap: toNullableFloat(form.dailyBudgetCap),
              cooldownSeconds: toNullableInt(form.cooldownSeconds),
              creativeCooldowns: toCreativeCooldownsPayload(
                form.creativeCooldowns,
                [form.defaultCreativeId, ...Object.values(segmentCreatives)],
              ),
              // Always send the provider allow-list (even when empty) so clearing
              // it persists; the backend merges it into any existing metadata
              // without clobbering import provenance.
              metadata: {
                selectedPublishers: form.metadata.selectedPublishers,
              },
            };

      let saved;
      if (editingCampaign) {
        saved = await updateCampaign(editingCampaign.uuid, payload);
      } else {
        saved = await addCampaign({
          ...payload,
          createdBy: currentUser || "admin",
        });
      }

      const campaignUuid = saved?.uuid ?? editingCampaign?.uuid;
      const nonEmpty = Object.fromEntries(
        Object.entries(segmentCreatives).filter(([, v]) => v),
      );
      if (
        campaignUuid &&
        form.type === CampaignType.Standard &&
        Object.keys(nonEmpty).length > 0
      ) {
        await performRequest(`/campaigns/${campaignUuid}/creatives`, {
          method: "PUT",
          body: JSON.stringify({ segmentCreatives: nonEmpty }),
        }).catch((err) =>
          console.error("Failed to save campaign creatives", err),
        );
      }

      await refreshCampaigns();
      goBack();
      return campaignUuid;
    } catch (error) {
      console.error("Failed to save campaign", error);
      if (typeof window !== "undefined") {
        window.alert(
          `Failed to save campaign${error?.message ? `: ${error.message}` : ""}. Please review the fields and try again.`,
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleModerate = async () => {
    if (!editingCampaign?.uuid) return;

    setIsModerateSaving(true);
    try {
      await submitCampaignForModeration(editingCampaign.uuid);
      await refreshCampaigns();
      window.alert("Campaign submitted for moderation.");
      goBack();
    } catch (error) {
      console.error("Failed to submit for moderation", error);
      window.alert(
        "Failed to submit for moderation: " +
          (error.message || "Unknown error"),
      );
    } finally {
      setIsModerateSaving(false);
    }
  };

  const handleApproveAndLaunch = async () => {
    const confirmed =
      typeof window !== "undefined"
        ? window.confirm(
            "Save changes, then approve and launch this campaign? It will go live immediately.",
          )
        : true;
    if (!confirmed) return;

    setIsApproving(true);
    try {
      const campaignUuid = await handleSubmit();
      if (!campaignUuid) {
        throw new Error("Missing campaign id after save");
      }
      await approveCampaign(campaignUuid);
      goBack();
    } catch (error) {
      console.error("Failed to approve and launch campaign", error);
      if (typeof window !== "undefined") {
        window.alert(
          "Failed to approve and launch campaign. Please try again.",
        );
      }
    } finally {
      setIsApproving(false);
    }
  };

  const handleDownloadPlan = async () => {
    if (!editingCampaign?.uuid) return;

    try {
      await downloadCampaignPlan(
        editingCampaign.uuid,
        form.name || editingCampaign.name || "Campaign",
      );
    } catch (error) {
      console.error("Failed to download media plan", error);
      window.alert("Failed to download media plan");
    }
  };

  if (isEdit && !editingCampaign) {
    return <p className="text-sm text-(--text-secondary)">Loading campaign…</p>;
  }

  const nextDisabled = !isStepValid(wizard.currentStep);

  const footer = (
    <>
      <Button
        variant="secondary"
        onClick={wizard.isFirstStep ? goBack : wizard.goBack}
        disabled={isSaving}
      >
        {wizard.isFirstStep ? "Cancel" : "Back"}
      </Button>
      {wizard.isLastStep ? (
        <Button onClick={handleSubmit} disabled={isSaving || isApproving}>
          {isSaving
            ? isEdit
              ? "Saving…"
              : "Creating…"
            : isEdit
              ? "Save changes"
              : "Create campaign"}
        </Button>
      ) : (
        <Button onClick={wizard.goNext} disabled={nextDisabled}>
          Next: {steps[wizard.currentStep + 1]?.label}
        </Button>
      )}
    </>
  );

  return (
    <WizardShell
      backLabel="Back to campaigns"
      onBack={goBack}
      steps={steps}
      currentStep={wizard.currentStep}
      onStepChange={wizard.goToStep}
      canGoToStep={wizard.canGoToStep}
      summary={
        <CampaignSummarySidebar
          form={form}
          isEditMode={isEdit}
          budget={budget}
          budgetLoading={budgetLoading}
          stats={stats}
          statsLoading={statsLoading}
        />
      }
      footer={footer}
    >
      {wizard.currentStep === 0 && (
        <Step1Basics
          isEdit={isEdit}
          form={form}
          onFieldChange={handleFieldChange}
        />
      )}
      {isPop && wizard.currentStep === 1 && (
        <PopTargetStep
          form={form}
          segments={segments}
          providers={providers}
          providersLoading={providersLoading}
          onPopChange={handlePopChange}
          onFieldChange={handleFieldChange}
        />
      )}
      {!isPop && wizard.currentStep === 1 && (
        <Step2Target
          form={form}
          segments={segments}
          providers={providers}
          providersLoading={providersLoading}
          providersError={providersError}
          onRetryProviders={refreshProviders}
          onFieldChange={handleFieldChange}
          onNumberChange={handleNumberChange}
          showProviderCost={currentUserType === "admin"}
        />
      )}
      {!isPop && wizard.currentStep === 2 && (
        <Step3Creatives
          form={form}
          segments={segments}
          creatives={creatives}
          onCreativeCreated={(created) =>
            setCreatives((prev) => [...prev, created])
          }
          segmentCreatives={segmentCreatives}
          onSegmentCreativesChange={setSegmentCreatives}
          onFieldChange={handleFieldChange}
        />
      )}
      {wizard.currentStep === steps.length - 1 && (
        <Step5Review
          form={form}
          segments={segments}
          providers={providers}
          creatives={creatives}
          segmentCreatives={segmentCreatives}
          isEditMode={isEdit}
          campaignUuid={editingCampaign?.uuid ?? null}
          onDownloadPlan={handleDownloadPlan}
          onModerate={handleModerate}
          isModerateSaving={isModerateSaving}
          canApprove={currentUserType === "admin"}
          onApproveAndLaunch={handleApproveAndLaunch}
          isApproving={isApproving}
        />
      )}
    </WizardShell>
  );
}
